import type { ReactElement, ReactNode } from 'react';
import { Badge, Button } from '@ff-it/ui';

type CommentBadgeProps = {
  /** The topic's channel key. Also the control's test id. */
  objectKey: string;
  /**
   * Live comments on the topic. Take it from a topic store where one exists,
   * since an embedded payload's count is only as fresh as its last fetch.
   */
  count: number;
  /**
   * Creates observed over the channel while the stream was closed. Only a
   * consumer holding a topic store can know this; a list row leaves it out.
   */
  updates?: number;
  /** Marks this control as the reason a stream is currently on screen. */
  active?: boolean;
  /** Opens the stream. Mutually exclusive with `children`. */
  onClick?: () => void;
  /** A link to render as the button instead, for rows that navigate. */
  children?: ReactNode;
};

/**
 * The comment affordance for one topic: a count, and the control that reaches
 * the comments. The button is not decoration — it is what the badge anchors to.
 *
 * Unseen arrivals take the badge over entirely, because a reader who has not
 * opened the stream has no reason to be watching the total.
 */
export function CommentBadge({
  objectKey,
  count,
  updates = 0,
  active,
  onClick,
  children,
}: CommentBadgeProps): ReactElement {
  const button = {
    testId: `comments-${objectKey}`,
    size: 'xxs',
    icon: 'message',
    variant: active ? ('primary' as const) : ('secondary' as const),
  } as const;

  return (
    <Badge count={updates > 0 ? updates : count} variant={updates > 0 ? 'primary' : 'secondary'}>
      {children ? (
        <Button {...button} asChild>
          {children}
        </Button>
      ) : (
        <Button {...button} onClick={onClick} />
      )}
    </Badge>
  );
}
